import useSWR, { mutate } from 'swr';
import settings from '/@/core/settings';
import { axFetch } from '/@/store/session/actions';
import type { PaginatedResponse } from '/@/types/core';
import type {
    CreateNotificationTrackerRequest,
    NotificationTracker,
    NotificationTrackerFilters,
    UpdateNotificationTrackerRequest,
} from '/@/types/notification-tracker';

/**
 * Fetches notification trackers from the API
 */
const getNotificationTrackers = async ({
    page,
    pageSize,
    codename,
    codenameIcontains,
    user,
    ordering,
}: {
    page: number;
    pageSize: number;
} & NotificationTrackerFilters): Promise<PaginatedResponse<NotificationTracker>> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());

    if (codename) {
        params.append('codename', codename);
    }
    if (codenameIcontains) {
        params.append('codename__icontains', codenameIcontains);
    }
    if (user) {
        params.append('user', user);
    }
    if (ordering) {
        params.append('ordering', ordering);
    }

    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/notifications/trackers/`, window.location.origin);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

/**
 * Hook to fetch notification trackers with pagination and filters
 */
export const useNotificationTrackers = (
    {
        page,
        pageSize = 10,
        codename,
        codenameIcontains,
        user,
        ordering,
    }: {
        page: number;
        pageSize?: number;
    } & NotificationTrackerFilters,
    options: { enabled?: boolean } = { enabled: true }
) => {
    return useSWR<PaginatedResponse<NotificationTracker>>(
        options.enabled
            ? {
                  key: 'useNotificationTrackers',
                  page,
                  pageSize,
                  codename,
                  codenameIcontains,
                  user,
                  ordering,
              }
            : null,
        (key) =>
            getNotificationTrackers({
                page: key.page,
                pageSize: key.pageSize,
                codename: key.codename,
                codenameIcontains: key.codenameIcontains,
                user: key.user,
                ordering: key.ordering,
            }),
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true,
        }
    );
};

/**
 * Hook to fetch notification trackers filtered by codename
 */
export const useNotificationTrackersByCodename = (
    {
        codename,
        codenameIcontains,
        page = 1,
        pageSize = 10,
    }: {
        codename?: string;
        codenameIcontains?: string;
        page?: number;
        pageSize?: number;
    },
    options: { enabled?: boolean } = { enabled: true }
) => {
    return useNotificationTrackers(
        {
            page,
            pageSize,
            codename,
            codenameIcontains,
        },
        options
    );
};

/**
 * Hook to fetch a single notification tracker by ID
 */
export const useNotificationTracker = ({ id }: { id: number }, options: { enabled?: boolean } = { enabled: true }) => {
    return useSWR<NotificationTracker>(
        options.enabled ? { key: 'useNotificationTracker', id } : null,
        async () => {
            const apiUrl = new URL(
                `${settings.urls.apiDomain}/core/v1/notifications/trackers/${id}/`,
                window.location.origin
            );
            const res = await axFetch.get(apiUrl.toString());
            return res.data;
        },
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );
};

/**
 * Creates a new notification tracker
 */
export const createNotificationTracker = async (
    data: CreateNotificationTrackerRequest
): Promise<NotificationTracker> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/notifications/trackers/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString(), data);

    // Invalidate all notification tracker queries
    mutate((key) => typeof key === 'object' && key?.key === 'useNotificationTrackers', undefined, {
        revalidate: true,
    });

    return res.data;
};

/**
 * Updates an existing notification tracker
 */
export const updateNotificationTracker = async (
    id: number,
    data: UpdateNotificationTrackerRequest
): Promise<NotificationTracker> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/notifications/trackers/${id}/`, window.location.origin);
    const res = await axFetch.patch(apiUrl.toString(), data);

    // Invalidate all notification tracker queries
    mutate((key) => typeof key === 'object' && key?.key === 'useNotificationTrackers', undefined, {
        revalidate: true,
    });
    mutate({ key: 'useNotificationTracker', id });

    return res.data;
};

/**
 * Marks a notification tracker as read
 */
export const markNotificationTrackerAsRead = async (id: number): Promise<NotificationTracker> => {
    return updateNotificationTracker(id, {
        readed_on: new Date().toISOString(),
    });
};

/**
 * Deletes a notification tracker
 */
export const deleteNotificationTracker = async (id: number): Promise<void> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/notifications/trackers/${id}/`, window.location.origin);
    await axFetch.delete(apiUrl.toString());

    // Invalidate all notification tracker queries
    mutate((key) => typeof key === 'object' && key?.key === 'useNotificationTrackers', undefined, {
        revalidate: true,
    });
    mutate({ key: 'useNotificationTracker', id });
};
